"use client";

import { useEffect, useRef, useState } from "react";
import { readStickyTopPx } from "./_lib/read-sticky-geometry";
import {
  resolveBounds,
  resolveRevealTop,
  type StickyMode,
} from "./_lib/sticky-math";

export function useStickyAside({ mode }: { mode: StickyMode }): {
  ref: React.RefObject<HTMLElement | null>;
  stickyTop: number;
} {
  const ref = useRef<HTMLElement>(null);
  const [stickyTop, setStickyTop] = useState(() => 80);
  const stickyTopPx = useRef(80);
  const sidebarHeight = useRef(0);
  const viewportHeight = useRef(0);
  const prevScrollY = useRef(0);
  const prevTop = useRef(80);
  const frameId = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const commit = (value: number) => {
      if (value !== prevTop.current) {
        prevTop.current = value;
        setStickyTop(value);
      }
    };

    const readGeometry = () => {
      stickyTopPx.current = readStickyTopPx();
      viewportHeight.current = window.innerHeight;
    };

    const recalc = () => {
      if (mode === "fixed") {
        commit(stickyTopPx.current);
        prevScrollY.current = window.scrollY;
        return;
      }

      const scrollY = window.scrollY;
      const scrollDeltaY = scrollY - prevScrollY.current;
      prevScrollY.current = scrollY;

      const { upperBound, lowerBound } = resolveBounds({
        stickyTopPx: stickyTopPx.current,
        viewportHeight: viewportHeight.current,
        sidebarHeight: sidebarHeight.current,
        mode,
      });

      const next = resolveRevealTop({
        prevTop: prevTop.current,
        scrollDeltaY,
        lowerBound,
        upperBound,
      });
      commit(next);
    };

    const scheduleRecalc = () => {
      if (frameId.current !== null) {
        return;
      }
      frameId.current = window.requestAnimationFrame(() => {
        frameId.current = null;
        recalc();
      });
    };

    readGeometry();
    sidebarHeight.current = el.getBoundingClientRect().height;
    prevScrollY.current = window.scrollY;
    prevTop.current = stickyTopPx.current;
    setStickyTop(stickyTopPx.current);

    const sidebarObserver = new ResizeObserver(() => {
      if (!ref.current) {
        return;
      }
      sidebarHeight.current = ref.current.getBoundingClientRect().height;
      scheduleRecalc();
    });
    sidebarObserver.observe(el);

    const handleResize = () => {
      readGeometry();
      scheduleRecalc();
    };

    window.addEventListener("scroll", scheduleRecalc, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    scheduleRecalc();

    return () => {
      if (frameId.current !== null) {
        cancelAnimationFrame(frameId.current);
        frameId.current = null;
      }
      sidebarObserver.disconnect();
      window.removeEventListener("scroll", scheduleRecalc);
      window.removeEventListener("resize", handleResize);
    };
  }, [mode]);

  return { ref, stickyTop };
}
